// Frontend-only value sets for the map surface. See enums/shell.ts for the rationale.

export const PAGE_MAP_LENSES = ["human", "agent"] as const;
export const PAGE_MAP_LENS = {
  HUMAN: "human",
  AGENT: "agent",
} as const satisfies Record<string, PageMapLens>;
export type PageMapLens = (typeof PAGE_MAP_LENSES)[number];

export const MAP_PLAN_TONES = ["ready", "warning"] as const;
export type MapPlanTone = (typeof MAP_PLAN_TONES)[number];
export const MAP_PLAN_TONE = {
  READY: "ready",
  WARNING: "warning",
} as const satisfies Record<string, MapPlanTone>;

export const MAP_ACTION_CATEGORYS = [
  "keep",
  "improve",
  "merge",
  "create",
  "retire",
] as const;
export type MapActionCategory = (typeof MAP_ACTION_CATEGORYS)[number];
export const MAP_ACTION_CATEGORY = {
  KEEP: "keep",
  IMPROVE: "improve",
  MERGE: "merge",
  CREATE: "create",
  RETIRE: "retire",
} as const satisfies Record<string, MapActionCategory>;

export const MAP_FUTURE_BRIEF_LEVELS = [
  "Page brief",
  "Flow brief",
  "Architecture brief",
] as const;
export type MapFutureBriefLevel = (typeof MAP_FUTURE_BRIEF_LEVELS)[number];
export const MAP_FUTURE_BRIEF_LEVEL = {
  PAGE_BRIEF: "Page brief",
  FLOW_BRIEF: "Flow brief",
  ARCHITECTURE_BRIEF: "Architecture brief",
} as const satisfies Record<string, MapFutureBriefLevel>;

export const MAP_ACTION_EVIDENCE_STATES = [
  "issue_backed",
  "structural",
  "provisional",
] as const;
export type MapActionEvidenceState =
  (typeof MAP_ACTION_EVIDENCE_STATES)[number];
export const MAP_ACTION_EVIDENCE_STATE = {
  ISSUE_BACKED: "issue_backed",
  STRUCTURAL: "structural",
  PROVISIONAL: "provisional",
} as const satisfies Record<string, MapActionEvidenceState>;

export const MAP_MIGRATION_WAVE_KEYS = [
  "foundation",
  "flow_fixes",
  "simplification",
  "additions",
] as const;
export type MapMigrationWaveKey = (typeof MAP_MIGRATION_WAVE_KEYS)[number];
export const MAP_MIGRATION_WAVE_KEY = {
  FOUNDATION: "foundation",
  FLOW_FIXES: "flow_fixes",
  SIMPLIFICATION: "simplification",
  ADDITIONS: "additions",
} as const satisfies Record<string, MapMigrationWaveKey>;

export const MAP_PREVIEW_VIEWS = [
  "baseline",
  "architecture",
  "migration",
] as const;
export type MapPreviewView = (typeof MAP_PREVIEW_VIEWS)[number];
export const MAP_PREVIEW_VIEW = {
  BASELINE: "baseline",
  ARCHITECTURE: "architecture",
  MIGRATION: "migration",
} as const satisfies Record<string, MapPreviewView>;

export const MAP_CONFIDENCES = ["strong", "some", "limited"] as const;
export type MapConfidence = (typeof MAP_CONFIDENCES)[number];
export const MAP_CONFIDENCE = {
  STRONG: "strong",
  SOME: "some",
  LIMITED: "limited",
} as const satisfies Record<string, MapConfidence>;

export const MAP_CHANGE_TYPES = [
  "create_hub",
  "consolidate_pages",
  "improve_pattern",
  "clarify_destination",
  "reorganise_group",
  "retain_structure",
  "unresolved",
] as const;
export type MapChangeType = (typeof MAP_CHANGE_TYPES)[number];
export const MAP_CHANGE_TYPE = {
  CREATE_HUB: "create_hub",
  CONSOLIDATE_PAGES: "consolidate_pages",
  IMPROVE_PATTERN: "improve_pattern",
  CLARIFY_DESTINATION: "clarify_destination",
  REORGANISE_GROUP: "reorganise_group",
  RETAIN_STRUCTURE: "retain_structure",
  UNRESOLVED: "unresolved",
} as const satisfies Record<string, MapChangeType>;

export const MAP_CHANGE_STATUSS = [
  "pending",
  "accepted",
  "adjusted",
  "unresolved",
  "dismissed",
] as const;
export type MapChangeStatus = (typeof MAP_CHANGE_STATUSS)[number];
export const MAP_CHANGE_STATUS = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  ADJUSTED: "adjusted",
  UNRESOLVED: "unresolved",
  DISMISSED: "dismissed",
} as const satisfies Record<string, MapChangeStatus>;

export const MAP_TARGET_LANES = ["organise", "pattern", "destination"] as const;
export type MapTargetLane = (typeof MAP_TARGET_LANES)[number];
export const MAP_TARGET_LANE = {
  ORGANISE: "organise",
  PATTERN: "pattern",
  DESTINATION: "destination",
} as const satisfies Record<string, MapTargetLane>;

export const MAP_EVIDENCE_KINDS = [
  "structure",
  "issue",
  "coverage",
  "inference",
] as const;
export type MapEvidenceKind = (typeof MAP_EVIDENCE_KINDS)[number];
export const MAP_EVIDENCE_KIND = {
  STRUCTURE: "structure",
  ISSUE: "issue",
  COVERAGE: "coverage",
  INFERENCE: "inference",
} as const satisfies Record<string, MapEvidenceKind>;

export const MAP_GROUP_ARCHETYPES = [
  "commerce",
  "editorial",
  "service",
  "general",
] as const;
export type MapGroupArchetype = (typeof MAP_GROUP_ARCHETYPES)[number];
export const MAP_GROUP_ARCHETYPE = {
  COMMERCE: "commerce",
  EDITORIAL: "editorial",
  SERVICE: "service",
  GENERAL: "general",
} as const satisfies Record<string, MapGroupArchetype>;

/**
 * WHAT a piece of map evidence is about. The producer states it; a consumer
 * that needs one specific piece selects on this rather than on the wording,
 * which is display copy and free to change.
 */
export const MAP_EVIDENCE_TOPICS = [
  "sibling_url_structure",
  "no_organising_page",
  "material_overlap",
  "shared_page_role",
  "mixed_structural_roles",
  "client_context_required",
  "onward_relationship",
  "path_break",
  "assessment_coverage",
  "shared_page_pattern",
  "issue_finding",
] as const;
export type MapEvidenceTopic = (typeof MAP_EVIDENCE_TOPICS)[number];
export const MAP_EVIDENCE_TOPIC = {
  SIBLING_URL_STRUCTURE: "sibling_url_structure",
  NO_ORGANISING_PAGE: "no_organising_page",
  MATERIAL_OVERLAP: "material_overlap",
  SHARED_PAGE_ROLE: "shared_page_role",
  MIXED_STRUCTURAL_ROLES: "mixed_structural_roles",
  CLIENT_CONTEXT_REQUIRED: "client_context_required",
  ONWARD_RELATIONSHIP: "onward_relationship",
  PATH_BREAK: "path_break",
  ASSESSMENT_COVERAGE: "assessment_coverage",
  SHARED_PAGE_PATTERN: "shared_page_pattern",
  ISSUE_FINDING: "issue_finding",
} as const satisfies Record<string, MapEvidenceTopic>;
